// src/pages/finances/InvoiceList.jsx
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Typography,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  GetApp as DownloadIcon,
  Edit as EditIcon,
  Payment as PaymentIcon,
  Receipt as InvoiceIcon,
  Send as SendIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';

// Import container components
import EntityListContainer from '../../components/common/EntityListContainer';
import ActionsMenu from '../../components/ui/ActionsMenu';
import { EntityStatusBadge } from '../../components/core';
import useConfirmDialog from '../../hooks/useConfirmationDialog';

// Import stores
import useAuthStore from '../../stores/authStore';
import useInvoiceStore from '../../stores/invoiceStore';
import useClientStore from '../../stores/clientStore';

// Helper for date formatting
import { format } from 'date-fns';
import { EntityCard } from '../../design-system/components/data';

const InvoiceList = () => {
  const navigate = useNavigate();
  
  // Get permissions from auth store
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission('finances', 'create');
  const canUpdate = hasPermission('finances', 'update');
  const canDelete = hasPermission('finances', 'delete');
  
  // Get store data and functions
  const { 
    invoices, 
    isLoading, 
    error, 
    pagination, 
    fetchInvoices,
    deleteInvoice,
    sendInvoice,
    generateInvoicePdf,
    cancelInvoice
  } = useInvoiceStore();
  
  const { clients, fetchClients } = useClientStore();
  
  // State for action menus
  const [actionMenus, setActionMenus] = useState({});
  
  // Confirmation dialog
  const [ConfirmDialog, confirm] = useConfirmDialog();
  
  // State for the current invoice being acted upon
  const [currentInvoice, setCurrentInvoice] = useState(null);
  
  // Helper functions
  const formatMoney = (amount, currency = "USD") => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'MMM d, yyyy');
  };
  
  const getClientName = (clientId) => {
    const client = clients.find(c => c._id === clientId);
    return client ? client.name : 'Unknown Client';
  };
  
  // Handle menu open/close
  const handleOpenMenu = useCallback((e, invoiceId) => {
    e.stopPropagation();
    setActionMenus(prev => ({
      ...prev,
      [invoiceId]: e.currentTarget
    }));
  }, []);
  
  const handleCloseMenu = useCallback((invoiceId) => {
    setActionMenus(prev => ({
      ...prev,
      [invoiceId]: null
    }));
  }, []);
  
  // Handle actions
  const handleAction = useCallback(async (action, invoice) => {
    setCurrentInvoice(invoice);
    handleCloseMenu(invoice._id);
    
    switch (action) {
      case 'view':
        navigate(`/invoices/${invoice._id}`);
        break;
      case 'edit':
        navigate(`/invoices/${invoice._id}/edit`);
        break;
      case 'delete':
        const confirmDelete = await confirm({
          title: 'Delete Invoice',
          content: `Are you sure you want to delete invoice #${invoice.invoiceNumber}? This action cannot be undone.`,
          confirmText: 'Delete',
          cancelText: 'Cancel',
          isDelete: true
        });
        
        if (confirmDelete) {
          try {
            await deleteInvoice(invoice._id);
          } catch (error) {
            console.error('Error deleting invoice:', error);
          }
        }
        break;
      case 'send':
        const confirmSend = await confirm({
          title: 'Send Invoice',
          content: `Are you sure you want to send invoice #${invoice.invoiceNumber} to ${invoice.billing?.contactEmail || 'the client'}?`,
          confirmText: 'Send',
          cancelText: 'Cancel',
        });
        
        if (confirmSend) {
          try {
            await sendInvoice(invoice._id, { recipient: invoice.billing?.contactEmail });
          } catch (error) {
            console.error('Error sending invoice:', error);
          }
        }
        break;
      case 'download':
        generateInvoicePdf(invoice._id);
        break;
      case 'cancel':
        const confirmCancel = await confirm({
          title: 'Cancel Invoice',
          content: `Are you sure you want to cancel invoice #${invoice.invoiceNumber}? This will mark it as cancelled and notify the client.`,
          confirmText: 'Cancel Invoice',
          cancelText: 'Keep Active',
          confirmButtonColor: 'error'
        });
        
        if (confirmCancel) {
          try {
            await cancelInvoice(invoice._id, "Cancelled by user");
          } catch (error) {
            console.error('Error cancelling invoice:', error);
          }
        }
        break;
      case 'payment':
        navigate(`/invoices/${invoice._id}?tab=payments`);
        break;
      default:
        break;
    }
  }, [navigate, confirm, deleteInvoice, sendInvoice, cancelInvoice, generateInvoicePdf]);
  
  // Filter configuration
  const filterConfig = [
    {
      name: 'status',
      label: 'Status',
      allLabel: 'All Statuses',
      options: [
        { value: 'draft', label: 'Draft' },
        { value: 'pending', label: 'Pending' },
        { value: 'paid', label: 'Paid' },
        { value: 'overdue', label: 'Overdue' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'refunded', label: 'Refunded' }
      ]
    },
    {
      name: 'clientId',
      label: 'Client',
      allLabel: 'All Clients',
      options: clients.map(client => ({
        value: client._id,
        label: client.name
      }))
    },
    {
      name: 'dateRange',
      label: 'Date Range',
      allLabel: 'All Time',
      options: [
        { value: 'thisMonth', label: 'This Month' },
        { value: 'lastMonth', label: 'Last Month' },
        { value: 'thisQuarter', label: 'This Quarter' },
        { value: 'lastQuarter', label: 'Last Quarter' },
        { value: 'thisYear', label: 'This Year' },
        { value: 'lastYear', label: 'Last Year' }
      ]
    }
  ];
  
  // Sort options
  const sortOptions = [
    { field: 'invoiceNumber', label: 'Invoice Number' },
    { field: 'clientId', label: 'Client Name' },
    { field: 'dates.issueDate', label: 'Issue Date' },
    { field: 'dates.dueDate', label: 'Due Date' },
    { field: 'amount.total', label: 'Amount' },
    { field: 'status', label: 'Status' }
  ];
  
  // Entity renderer for grid view
  const renderInvoice = useCallback((invoice) => {
    return (
      <>
        <EntityCard
          title={`Invoice #${invoice.invoiceNumber}`}
          description={
            typeof invoice.clientId === 'object' 
              ? invoice.clientId?.name 
              : getClientName(invoice.clientId)
          }
          status={
            <EntityStatusBadge status={invoice.status} entityType="invoice" size="small" />
          }
          chips={[
            invoice.dates?.dueDate && (
              <Chip 
                key="due" 
                label={`Due: ${formatDate(invoice.dates.dueDate)}`} 
                size="small" 
                color="default"
                variant="outlined"
              />
            )
          ]}
          stats={[
            { label: 'Amount', value: formatMoney(invoice.amount?.total, invoice.currency?.code) },
            { label: 'Issue Date', value: formatDate(invoice.dates?.issueDate) }
          ]}
          linkTo={`/invoices/${invoice._id}`}
          menuButtonProps={{
            onClick: (e) => handleOpenMenu(e, invoice._id)
          }}
        />
        
        {/* Separate actions menu */}
        <ActionsMenu
          anchorEl={actionMenus[invoice._id]}
          open={Boolean(actionMenus[invoice._id])}
          onClose={() => handleCloseMenu(invoice._id)}
          actions={[
            {
              label: 'View Details',
              onClick: () => handleAction('view', invoice),
              icon: <ViewIcon fontSize="small" />
            },
            ...(canUpdate ? [{
              label: 'Edit Invoice',
              onClick: () => handleAction('edit', invoice),
              icon: <EditIcon fontSize="small" />
            }] : []),
            {
              label: 'Download PDF',
              onClick: () => handleAction('download', invoice),
              icon: <DownloadIcon fontSize="small" />
            },
            ...(['draft', 'pending'].includes(invoice.status) && canUpdate ? [{
              label: 'Send to Client',
              onClick: () => handleAction('send', invoice),
              icon: <SendIcon fontSize="small" />
            }] : []),
            ...(!['paid', 'cancelled', 'refunded', 'writeoff'].includes(invoice.status) && canUpdate ? [{
              label: 'Record Payment',
              onClick: () => handleAction('payment', invoice),
              icon: <PaymentIcon fontSize="small" />
            }] : []),
            ...(!['cancelled', 'refunded', 'writeoff'].includes(invoice.status) && canUpdate ? [{
              label: 'Cancel Invoice',
              onClick: () => handleAction('cancel', invoice),
              icon: <CancelIcon fontSize="small" />
            }] : [])
          ]}
          dangerActions={canDelete ? [
            {
              label: 'Delete Invoice',
              onClick: () => handleAction('delete', invoice),
              icon: <DeleteIcon fontSize="small" />
            }
          ] : []}
        />
      </>
    );
  }, [canUpdate, canDelete, actionMenus, handleOpenMenu, handleCloseMenu, handleAction]);
  
  // Define table columns
  const tableColumns = [
    { 
      id: 'invoiceNumber', 
      label: 'Invoice #',
      renderCell: (invoice) => (
        <Typography variant="body2" fontWeight={500}>
          {invoice.invoiceNumber}
        </Typography>
      )
    },
    { 
      id: 'client', 
      label: 'Client',
      renderCell: (invoice) => (
        <Typography variant="body2">
          {typeof invoice.clientId === 'object' 
            ? invoice.clientId?.name 
            : getClientName(invoice.clientId)}
        </Typography>
      )
    },
    { 
      id: 'issueDate', 
      label: 'Issue Date',
      renderCell: (invoice) => (
        <Typography variant="body2">
          {formatDate(invoice.dates?.issueDate)}
        </Typography>
      )
    },
    { 
      id: 'dueDate', 
      label: 'Due Date',
      renderCell: (invoice) => (
        <Typography variant="body2">
          {formatDate(invoice.dates?.dueDate)}
        </Typography>
      )
    },
    { 
      id: 'amount', 
      label: 'Amount',
      align: 'right',
      renderCell: (invoice) => (
        <Typography variant="body2" fontWeight={500}>
          {formatMoney(invoice.amount?.total, invoice.currency?.code)}
        </Typography>
      )
    },
    { 
      id: 'status', 
      label: 'Status',
      align: 'center',
      renderCell: (invoice) => (
        <EntityStatusBadge status={invoice.status} entityType="invoice" size="small" />
      )
    }
  ];
  
  // Configure table row actions for table view
  const getTableRowActions = useCallback((invoice) => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title="View">
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              handleAction('view', invoice);
            }}
          >
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Download">
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              handleAction('download', invoice);
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        {canUpdate && (
          <Tooltip title="Edit">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                handleAction('edit', invoice);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        
        {canDelete && (
          <Tooltip title="Delete">
            <IconButton 
              size="small" 
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleAction('delete', invoice);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }, [canUpdate, canDelete, handleAction]);
  
  return (
    <>
      <EntityListContainer
        title="Invoices"
        description="Manage your invoices and payments"
        fetchData={fetchInvoices}
        renderEntity={renderInvoice}
        initialFilters={{
          status: 'all',
          clientId: 'all',
          dateRange: 'all'
        }}
        initialSort={{ field: 'dates.issueDate', direction: 'desc' }}
        filterConfig={filterConfig}
        sortOptions={sortOptions}
        
        // View options
        availableViews={['grid', 'table']}
        defaultView="table"
        
        // Table configuration
        tableConfig={{
          columns: tableColumns,
          onRowClick: (invoice) => navigate(`/invoices/${invoice._id}`),
          renderRowActions: getTableRowActions
        }}
        
        // Use direct store values
        entities={invoices}
        isLoading={isLoading}
        error={error}
        metadata={pagination}
        
        actions={{
          canCreate,
          createPath: '/invoices/create',
          createLabel: 'Create Invoice'
        }}
        emptyState={{
          title: 'No Invoices Yet',
          description: 'Get started by creating your first invoice.',
          icon: <InvoiceIcon sx={{ fontSize: 64 }} />,
          getFilteredTitle: () => 'No Matching Invoices',
          getFilteredDescription: () => 'Try adjusting your search or filters to find what you\'re looking for.'
        }}
      />
      
      <ConfirmDialog />
    </>
  );
};

export default InvoiceList;